// /sync-health — Admin Sync Health (G2.24)
//
// Cross-tenant sync conflict monitoring + resolution surface for both
// tenant-level managers and platform staff.
//
// Audience tiers:
//   - OWNER (50) / MANAGER (30): see and resolve conflicts in their own
//     tenant only. The tenant picker is hidden — `adminSyncService` calls
//     are made WITHOUT a `tenantId` override, so the backend falls back to
//     the JWT tenant via @CurrentTenant().
//   - SUPER_ADMIN (100) / ADMIN (80) / MODERATOR (60): see a tenant picker
//     loaded from `tenantService.list()`. Selecting a tenant scopes every
//     subsequent fetch to that tenant via the `?tenant_id=` query param
//     escape hatch. Until they pick one, the page shows an empty-state
//     prompt instead of running unscoped queries.
//
// What this page DOES NOT include (deliberately):
//   - Queue / History tabs from the POS variant. Those are cashier-side
//     concerns (offline state, manual push triggers, pull log) and have no
//     audience here. Conflict resolution is the only platform-relevant slice.
//
// Realtime (G2.25): the page subscribes to the WebSocket gateway via
// `useSyncConflictsRealtime`. Platform staff (SUPER_ADMIN/ADMIN/MODERATOR)
// receive the cross-tenant `sync:conflict:platform` event from the
// `platform:sync-health` room (joined automatically by EventsGateway on
// connect). OWNER/MANAGER receive the existing tenant-scoped `sync:conflict`
// event. The hook fires a warning toast and refreshes the conflict list when
// the inbound event matches the active tenant filter.
//
// Visual: Orio360 "No Borders, Mono Data, Glass Depth" — amber/rose hero
// gradient (conflict-themed), StatCard KPI row, sortable DataTable.
//
// Traceability: FR-SYNC-013, US-BILL-019, AGENTS.md ("Conflicts are queued
// for Manager/Owner review").

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Badge, Button, Select, Spinner, Skeleton, TextArea, ConfirmDialog, Alert,
  ErrorBoundary, ConflictViewRouter, ENTITY_VIEWS, ConflictResolutionPanel,
  PageContainer, DataTable, EmptyState, Drawer, StatCard, SyncStatusBadge,
  Pagination, useToast,
} from '@bbpos/ui';
import type { ColumnDef, BulkAction } from '@bbpos/ui';
import { formatDateTime as formatDateTimeShared } from '@bbpos/utils';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { useSyncConflictsRealtime } from '@/hooks/use-sync-conflicts-realtime';
import { connectSocket, disconnectSocket, subscribeSocketStatus, type SocketStatus } from '@/lib/socket-client';
import { GLASS, GLASS_FILTER, cn } from '@/constants/design-tokens';
import {
  RESOLUTION_CONFIG,
  ENTITY_TYPE_CONFIG,
  RESOLUTION_OPTIONS,
  truncate,
  relativeTimeSync,
} from '@/lib/sync-constants';
import { adminSyncService, type SyncConflict, type SyncConflictDetail } from '@/services/sync.service';
import { tenantService } from '@/services/tenant.service';

// ─── Constants ────────────────────────────────────────────────────────────────

const CONFLICT_PAGE_SIZE = 20;
const TENANT_PICKER_LIMIT = 100;

const PLATFORM_ROLES = new Set(['SUPER_ADMIN', 'ADMIN', 'MODERATOR']);

// Use shared formatter from @bbpos/utils (Asia/Kolkata, en-IN).
function formatDateTime(iso: string | undefined | null): string {
  return formatDateTimeShared(iso);
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface TenantOption {
  value: string;
  label: string;
}

// ─── Socket status → SyncStatusBadge mapping ─────────────────────────────────

function socketStatusToSyncStatus(s: SocketStatus): 'synced' | 'syncing' | 'offline' {
  if (s === 'connected') return 'synced';
  if (s === 'connecting' || s === 'reconnecting') return 'syncing';
  return 'offline';
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function SyncIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.582m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function AlertTriangleIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function CheckCircleIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function ClockIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

// ─── Conflict detail panel ────────────────────────────────────────────────────

function ConflictDetailPanel({
  conflict,
  isResolving,
  resolvingAs,
  note,
  onNoteChange,
  onResolve,
  onDiscard,
  userRole,
}: {
  conflict: SyncConflictDetail;
  isResolving: boolean;
  resolvingAs: string | null;
  note: string;
  onNoteChange: (v: string) => void;
  onResolve: (resolution: string, mergedVersion?: Record<string, unknown>) => void;
  onDiscard: () => void;
  userRole: string;
}) {
  const isResolved = conflict.resolution !== 'PENDING';
  const resCfg = RESOLUTION_CONFIG[conflict.resolution];

  // GAP-7: AUTH_SPEC — financial conflicts (BILL/EXPENSE) require OWNER+.
  // A MANAGER sees the diff but can't resolve. Platform roles (SUPER_ADMIN,
  // ADMIN, MODERATOR) are elevated and always allowed.
  const isFinancialEntity = conflict.entity_type === 'BILL' || conflict.entity_type === 'EXPENSE';
  const resolveBlocked = isFinancialEntity && userRole === 'MANAGER';

  return (
    <div className="space-y-5 p-4">
      {/* Diff section */}
      <div className={cn(GLASS, 'p-4')}>
        <ErrorBoundary fallback={
          <Alert
            variant="danger"
            title="Could not render conflict details"
            description="The conflict payload is malformed. Choose a resolution below to proceed, or contact support."
          />
        }>
          <ConflictViewRouter
            entityType={conflict.entity_type}
            clientVersion={
              conflict.client_version.changes && typeof conflict.client_version.changes === 'object'
                ? { ...conflict.server_version, ...(conflict.client_version.changes as Record<string, unknown>) }
                : conflict.client_version
            }
            serverVersion={conflict.server_version}
            deviceId={conflict.device_id}
            conflictMessage={conflict.resolution_note ?? undefined}
          />
        </ErrorBoundary>
      </div>

      {/* Device line for non-per-entity views (those views render their own header) */}
      {conflict.device_id && !ENTITY_VIEWS.has(conflict.entity_type) && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Device: <span className="font-mono">{conflict.device_id}</span>
        </p>
      )}

      {isResolved ? (
        <div className={cn(GLASS, 'p-4 space-y-2')}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500 mb-2">
            Resolution
          </p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-neutral-600 dark:text-neutral-400">Resolved as</span>
            <Badge variant={resCfg?.tone ?? 'info'} size="sm">{resCfg?.label ?? conflict.resolution}</Badge>
            {conflict.resolved_at && (
              <span className="text-xs text-neutral-400">{formatDateTime(conflict.resolved_at)}</span>
            )}
          </div>
          {conflict.resolution_note && (
            <p className="text-xs italic text-neutral-500 dark:text-neutral-400 mt-1">{conflict.resolution_note}</p>
          )}
        </div>
      ) : (
        <div className={cn(GLASS, 'p-4 space-y-4')}>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
            Choose Resolution
          </p>

          {/* Only the outcomes the API offers for this conflict (sync plan
              Phase 6). GAP-7: a MANAGER cannot resolve BILL / EXPENSE — the
              backend enforces it too. */}
          <ConflictResolutionPanel
            entityType={conflict.entity_type}
            kind={conflict.kind}
            allowedResolutions={conflict.allowed_resolutions}
            clientVersion={conflict.client_version}
            serverVersion={conflict.server_version}
            mergedSuggestion={conflict.merged_version_suggestion}
            resolutionError={conflict.resolution_error}
            note={note}
            onNoteChange={onNoteChange}
            busy={isResolving}
            busyAs={resolvingAs}
            blocked={resolveBlocked}
            onResolve={onResolve}
            onDiscard={onDiscard}
          />
        </div>
      )}
    </div>
  );
}

// ─── Tenant picker ────────────────────────────────────────────────────────────

function TenantPicker({
  options,
  value,
  onChange,
  isLoading,
}: {
  options: TenantOption[];
  value: string;
  onChange: (v: string) => void;
  isLoading: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
        Tenant
      </span>
      <div className="w-64">
        <Select
          options={[{ value: '', label: 'Select a tenant...' }, ...options]}
          value={value}
          onChange={(v) => onChange(typeof v === 'string' ? v : (v[0] ?? ''))}
          placeholder="Select a tenant..."
          disabled={isLoading}
        />
      </div>
      {isLoading && <Spinner size="sm" />}
    </div>
  );
}

// ─── Mobile card ──────────────────────────────────────────────────────────────

function ConflictMobileCard({
  conflict,
  isSelected,
  onClick,
}: {
  conflict: SyncConflict;
  isSelected: boolean;
  onClick: () => void;
}) {
  const entityCfg = ENTITY_TYPE_CONFIG[conflict.entity_type];
  const resCfg = RESOLUTION_CONFIG[conflict.resolution];

  // Relative time for age indicator
  const age = relativeTimeSync(conflict.created_at);
  const isOld = conflict.resolution === 'PENDING' &&
    Date.now() - new Date(conflict.created_at).getTime() > 24 * 60 * 60 * 1000;

  return (
    <div
      className={cn(
        GLASS,
        'p-4 animate-slide-up cursor-pointer transition-all duration-200 hover:shadow-md',
        isSelected && 'ring-2 ring-brand-500/30',
        isOld && conflict.resolution === 'PENDING' && 'border-l-4 border-l-danger-500',
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={cn(
            'h-7 w-7 rounded-lg flex items-center justify-center text-white',
            entityCfg?.tone === 'danger' ? 'bg-danger-500' :
            entityCfg?.tone === 'warning' ? 'bg-warning-500' :
            entityCfg?.tone === 'success' ? 'bg-success-500' :
            'bg-brand-500',
          )}>
            <SyncIcon className="h-3.5 w-3.5" />
          </div>
          <Badge variant={entityCfg?.tone ?? 'info'} size="sm">
            {entityCfg?.label ?? conflict.entity_type}
          </Badge>
        </div>
        <Badge variant={resCfg?.tone ?? 'warning'} size="sm">
          {resCfg?.label ?? conflict.resolution}
        </Badge>
      </div>
      <div className="flex items-center justify-between text-xs text-neutral-500 dark:text-neutral-400">
        <span className="font-mono">{truncate(conflict.client_id, 16)}</span>
        <div className="text-right">
          <span>{formatDateTime(conflict.created_at)}</span>
          {age && <span className="block text-[10px] text-neutral-400">{age}</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Styled empty state icon ─────────────────────────────────────────────────

function SyncEmptyStateIcon() {
  return (
    <div className="h-16 w-16 rounded-2xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center">
      <SyncIcon className="h-7 w-7" />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdminSyncHealthPage() {
  const { profile, isLoading: authLoading } = useRequireAuth();

  const isPlatformUser = useMemo(
    () => PLATFORM_ROLES.has(profile?.role ?? ''),
    [profile?.role],
  );

  // ── Socket status for SyncStatusBadge ──
  const [socketStatus, setSocketStatus] = useState<SocketStatus>('idle');
  useEffect(() => {
    return subscribeSocketStatus(setSocketStatus);
  }, []);

  // ── Tenant picker state (platform users only) ──
  //
  // G2.26: Initial value may be supplied via `?tenant=<uuid>` — the Sync
  // Health Overview widget on the platform dashboard drills through with
  // this param so the picker is pre-scoped to the clicked tenant. The
  // sync happens in a post-auth effect below (we can't initialize from the
  // URL synchronously because `profile` may still be loading on mount).
  const [searchParams] = useSearchParams();
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');

  // ── Conflict list state ──
  const [conflicts, setConflicts] = useState<SyncConflict[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>({ page: 1, limit: CONFLICT_PAGE_SIZE, total: 0, totalPages: 0 });
  const [resolutionFilter, setResolutionFilter] = useState('');
  // Server-side sort so ordering spans the whole dataset, not just the
  // current 20-row page. `null` = server default (created_at DESC).
  const [sortState, setSortState] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  // ── Conflict detail state ──
  const [selectedConflict, setSelectedConflict] = useState<SyncConflictDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [resolvingAs, setResolvingAs] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // Bulk discard: the selected ids wait here until a note is given.
  const [bulkDiscardIds, setBulkDiscardIds] = useState<string[] | null>(null);
  const [bulkDiscardNote, setBulkDiscardNote] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Bulk selection state ──
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isBulkResolving, setIsBulkResolving] = useState(false);

  // ── Aria-live region for real-time alerts ──
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  // Refs so async callbacks can read the latest filter/tenant without
  // re-binding listeners on every render.
  const filterRef = useRef(resolutionFilter);
  filterRef.current = resolutionFilter;
  const tenantRef = useRef(selectedTenantId);
  tenantRef.current = selectedTenantId;
  const sortRef = useRef(sortState);
  sortRef.current = sortState;

  // ── Effective tenant scope for fetches ──
  // Tenant users → undefined (backend falls through to @CurrentTenant).
  // Platform users → must pick first; otherwise the page shows an empty
  // state and never queries.
  const canQuery = !isPlatformUser || !!selectedTenantId;

  // ── G2.26: Honor `?tenant=<uuid>` drill-through from the platform
  // dashboard Sync Health Overview widget. Only platform users may scope
  // to another tenant; tenant-level OWNER/MANAGER silently ignore the
  // param. Runs once after auth resolves so we're certain about the role.
  const urlTenantRef = useRef(false);
  useEffect(() => {
    if (authLoading || !profile || urlTenantRef.current) return;
    urlTenantRef.current = true;
    if (!isPlatformUser) return;
    const fromUrl = searchParams.get('tenant') ?? '';
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (UUID_RE.test(fromUrl)) {
      setSelectedTenantId(fromUrl);
    }
  }, [authLoading, profile, isPlatformUser, searchParams]);

  // ── Load tenant list (platform users only) ──
  useEffect(() => {
    if (!isPlatformUser) return;
    let cancelled = false;
    setTenantsLoading(true);
    tenantService
      .list({ page: 1, limit: TENANT_PICKER_LIMIT })
      .then((res) => {
        if (cancelled) return;
        const opts = (res.data ?? []).map((t) => ({ value: t.id, label: t.business_name }));
        setTenantOptions(opts);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(`Could not load tenant list: ${(err as Error).message}`);
      })
      .finally(() => {
        if (!cancelled) setTenantsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPlatformUser]);

  // ── Load conflicts ──
  const loadConflicts = useCallback(
    async (params?: { resolution?: string; page?: number }) => {
      if (!canQuery) {
        setConflicts([]);
        setMeta({ page: 1, limit: CONFLICT_PAGE_SIZE, total: 0, totalPages: 0 });
        return;
      }
      setIsLoading(true);
      setLoadError(null);
      try {
        const res = await adminSyncService.listConflicts({
          resolution: params?.resolution ?? (filterRef.current || undefined),
          page: params?.page ?? 1,
          limit: CONFLICT_PAGE_SIZE,
          tenantId: tenantRef.current || undefined,
          sort: sortRef.current?.col,
          order: sortRef.current?.dir,
        });
        setConflicts(res.data);
        setMeta(res.meta);
        setLastRefreshed(new Date());
      } catch (err) {
        setLoadError(`Failed to load conflicts: ${(err as Error).message}`);
        setConflicts([]);
      } finally {
        setIsLoading(false);
      }
    },
    [canQuery],
  );

  useEffect(() => {
    if (authLoading) return;
    void loadConflicts();
    // Reset detail when tenant context changes
    setSelectedConflict(null);
    setDrawerOpen(false);
    setNote('');
  }, [authLoading, selectedTenantId, loadConflicts]);

  // ── Realtime: open the shared admin socket on mount, tear down on unmount.
  // The gateway joins `platform:sync-health` automatically for SUPER_ADMIN /
  // ADMIN / MODERATOR; tenant users get the existing `tenant:{id}` room.
  useEffect(() => {
    if (authLoading || !profile) return;
    connectSocket();
    return () => {
      disconnectSocket();
    };
  }, [authLoading, profile]);

  // tenant_id → display name lookup for the cross-tenant toast labels.
  // Sourced from the existing tenant picker cache so we don't double-fetch.
  const tenantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of tenantOptions) map.set(opt.value, opt.label);
    return map;
  }, [tenantOptions]);

  useSyncConflictsRealtime({
    onRefresh: () => {
      setLiveAnnouncement(`New sync conflict detected at ${new Date().toLocaleTimeString('en-IN')}`);
      void loadConflicts();
    },
    selectedTenantId: isPlatformUser ? selectedTenantId || null : null,
    tenantNameById,
  });

  const handleFilterChange = useCallback(
    (val: string | string[]) => {
      const v = typeof val === 'string' ? val : val[0] ?? '';
      setResolutionFilter(v);
      void loadConflicts({ resolution: v, page: 1 });
    },
    [loadConflicts],
  );

  const handleRowClick = useCallback(
    async (id: string) => {
      setIsLoadingDetail(true);
      setDrawerOpen(true);
      try {
        const detail = await adminSyncService.getConflict(id, tenantRef.current || undefined);
        setSelectedConflict(detail);
      } catch (err) {
        setLoadError(`Failed to load conflict detail: ${(err as Error).message}`);
      } finally {
        setIsLoadingDetail(false);
      }
    },
    [],
  );

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedConflict(null);
    setNote('');
    setResolvingAs(null);
  }, []);

  const handleResolve = useCallback(
    async (resolution: string, mergedVersion?: Record<string, unknown>) => {
      if (!selectedConflict) return;
      setIsResolving(true);
      setResolvingAs(resolution);
      try {
        await adminSyncService.resolveConflict(selectedConflict.id, {
          resolution,
          note: note || undefined,
          ...(mergedVersion ? { merged_version: mergedVersion } : {}),
          tenantId: tenantRef.current || undefined,
        });
        // Refresh both list + detail to reflect new resolution state
        await loadConflicts();
        const refreshed = await adminSyncService.getConflict(
          selectedConflict.id,
          tenantRef.current || undefined,
        );
        setSelectedConflict(refreshed);
        setNote('');
      } catch (err) {
        setLoadError(`Failed to resolve conflict: ${(err as Error).message}`);
      } finally {
        setIsResolving(false);
        setResolvingAs(null);
      }
    },
    [selectedConflict, note, loadConflicts],
  );

  const handleDiscard = useCallback(async () => {
    await handleResolve('DISCARDED');
    setShowDiscardConfirm(false);
  }, [handleResolve]);

  // ── Bulk resolution handler ──
  const { toast } = useToast();
  const handleBulkResolve = useCallback(
    async (resolution: string, ids: string[], bulkNote?: string) => {
      if (ids.length === 0) return;
      setIsBulkResolving(true);
      try {
        const result = await adminSyncService.bulkResolveConflicts({
          conflict_ids: ids,
          resolution,
          ...(bulkNote ? { note: bulkNote } : {}),
          tenantId: tenantRef.current || undefined,
        });
        toast({
          variant: result.failed > 0 ? 'warning' : 'success',
          title: `Bulk resolve: ${result.resolved} resolved, ${result.failed} failed`,
        });
        setSelectedRows([]);
        await loadConflicts();
      } catch (err) {
        setLoadError(`Bulk resolve failed: ${(err as Error).message}`);
      } finally {
        setIsBulkResolving(false);
      }
    },
    [loadConflicts, toast],
  );

  const bulkActions: BulkAction[] = useMemo(() => [
    {
      // Accepts refused offline bills as sold (Owner) or applies the till's version.
      // Each conflict is checked by the API; ones that do not offer it fail individually.
      label: 'Accept / use till version',
      onClick: (ids: string[]) => { void handleBulkResolve('CLIENT_WINS', ids); },
    },
    {
      label: 'Keep server version',
      onClick: (ids: string[]) => { void handleBulkResolve('SERVER_WINS', ids); },
    },
    {
      // A discard needs a reason (US-SYNC-003): ask for one note for the batch.
      label: 'Discard selected…',
      onClick: (ids: string[]) => { setBulkDiscardIds(ids); },
    },
  ], [handleBulkResolve]);

  // ── KPI stats derived from current page data ──
  // NOTE: These counts reflect the current page only (max CONFLICT_PAGE_SIZE),
  // NOT the entire dataset. Labels must reflect this to avoid misleading staff.
  const pendingCount = useMemo(
    () => conflicts.filter((c) => c.resolution === 'PENDING').length,
    [conflicts],
  );
  const resolvedCount = useMemo(
    () => conflicts.filter((c) => c.resolution !== 'PENDING').length,
    [conflicts],
  );
  const oldestPendingAge = useMemo(() => {
    const pending = conflicts.filter((c) => c.resolution === 'PENDING');
    if (pending.length === 0) return null;
    const oldest = pending.reduce((min, c) =>
      new Date(c.created_at).getTime() < new Date(min.created_at).getTime() ? c : min,
    );
    const ms = Date.now() - new Date(oldest.created_at).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const mins = Math.floor(ms / 60_000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ${mins % 60}m`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  }, [conflicts]);
  const oldestPendingIsStale = useMemo(() => {
    const pending = conflicts.filter((c) => c.resolution === 'PENDING');
    if (pending.length === 0) return false;
    const oldest = pending.reduce((min, c) =>
      new Date(c.created_at).getTime() < new Date(min.created_at).getTime() ? c : min,
    );
    return Date.now() - new Date(oldest.created_at).getTime() > 24 * 60 * 60 * 1000;
  }, [conflicts]);

  // ── DataTable columns ──
  const columns: ColumnDef<SyncConflict>[] = useMemo(() => [
    {
      key: 'entity_type',
      header: 'Entity Type',
      sortable: true,
      render: (_v: unknown, row: SyncConflict) => {
        const entityCfg = ENTITY_TYPE_CONFIG[row.entity_type];
        const isStale = row.resolution === 'PENDING' &&
          Date.now() - new Date(row.created_at).getTime() > 86_400_000;
        return (
          <div className="flex items-center gap-2">
            {isStale && <span className="h-2 w-2 rounded-full bg-danger-500 animate-pulse shrink-0" title="Pending over 24 hours" />}
            <Badge variant={entityCfg?.tone ?? 'info'} size="sm">{entityCfg?.label ?? row.entity_type}</Badge>
          </div>
        );
      },
    },
    {
      key: 'client_id',
      header: 'Client ID',
      render: (_v: unknown, row: SyncConflict) => (
        <span className="font-mono text-xs text-neutral-600 dark:text-neutral-400">{truncate(row.client_id, 24)}</span>
      ),
    },
    {
      key: 'resolution',
      header: 'Status',
      sortable: true,
      render: (_v: unknown, row: SyncConflict) => {
        const resCfg = RESOLUTION_CONFIG[row.resolution];
        return <Badge variant={resCfg?.tone ?? 'warning'} size="sm">{resCfg?.label ?? row.resolution}</Badge>;
      },
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (_v: unknown, row: SyncConflict) => (
        <div>
          <span className="text-xs font-mono tabular-nums text-neutral-600 dark:text-neutral-300">{formatDateTime(row.created_at)}</span>
          <span className="block text-[10px] text-neutral-400 dark:text-neutral-500">{relativeTimeSync(row.created_at)}</span>
        </div>
      ),
    },
    {
      key: 'id',
      header: '',
      render: (_v: unknown, row: SyncConflict) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); void handleRowClick(row.id); }}
          aria-label="View conflict details"
          className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-200 transition-colors duration-200"
        >
          Details
        </button>
      ),
    },
  ], [handleRowClick]);

  // ── Drawer title ──
  const drawerTitle = useMemo(() => {
    if (!selectedConflict) return 'Conflict Detail';
    const label = ENTITY_TYPE_CONFIG[selectedConflict.entity_type]?.label ?? selectedConflict.entity_type;
    return `Conflict Detail — ${label}`;
  }, [selectedConflict]);

  // ── Last refreshed display ──
  const lastRefreshedText = useMemo(() => {
    if (!lastRefreshed) return '';
    return relativeTimeSync(lastRefreshed.toISOString()) || 'just now';
  }, [lastRefreshed]);

  // ── Render ──

  if (authLoading || !profile) {
    return <PageContainer loading />;
  }

  return (
    <PageContainer
      title="Sync Health"
      subtitle={isPlatformUser
        ? 'Inspect and resolve offline-sync conflicts across tenants'
        : 'Review and resolve offline-sync conflicts for your tenant'}
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Sync Health' }]}
      actions={
        <div className="flex items-center gap-3">
          <SyncStatusBadge status={socketStatusToSyncStatus(socketStatus)} />
          {lastRefreshedText && (
            <span className="text-xs text-neutral-400 dark:text-neutral-500 hidden sm:inline">
              {lastRefreshedText}
            </span>
          )}
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void loadConflicts()}
            disabled={!canQuery || isLoading}
          >
            Refresh
          </Button>
        </div>
      }
    >
      {/* Aria-live region for screen readers */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </div>

      {/* Hero gradient strip — amber/rose conflict-themed */}
      <div className="bg-gradient-to-r from-amber-600 to-rose-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(245,158,11,0.45)] mb-6 relative overflow-hidden animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-white/5 blur-2xl" />
        <div className="relative flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <SyncIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-bold tracking-tight text-white">Conflict Resolution</h2>
            <p className="text-amber-100 mt-0.5 text-sm">
              {isPlatformUser
                ? 'Select a tenant to inspect pending offline-sync conflicts and choose resolutions'
                : 'Review pending offline-sync conflicts and choose how to resolve each one'}
            </p>
          </div>
          {/* T09: the tax-mismatch list reads the signed-in tenant only. */}
          {!isPlatformUser && (
            <Link
              to="/sync-health/tax-mismatches"
              className="ml-auto rounded-full bg-white/20 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-white/30"
            >
              Tax mismatches
            </Link>
          )}
          {/* T19: offline card/UPI payments waiting for a manager's check. */}
          {!isPlatformUser && (
            <Link
              to="/sync-health/offline-payments"
              className="rounded-full bg-white/20 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-white/30"
            >
              Offline payments
            </Link>
          )}
        </div>
      </div>

      {/* KPI cards strip */}
      {canQuery && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 animate-fade-in">
          {isLoading && conflicts.length === 0 ? (
            <>
              {[1, 2, 3, 4].map((i) => <StatCard key={i} title="" value="" loading />)}
            </>
          ) : (
            <>
              <div className="animate-slide-up">
                <StatCard
                  title="Total Conflicts"
                  value={meta.total.toLocaleString('en-IN')}
                  icon={<SyncIcon />}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
                <StatCard
                  title="Pending (this page)"
                  value={pendingCount.toLocaleString('en-IN')}
                  icon={<AlertTriangleIcon />}
                  anomaly={pendingCount > 0}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
                <StatCard
                  title="Resolved (this page)"
                  value={resolvedCount.toLocaleString('en-IN')}
                  icon={<CheckCircleIcon />}
                />
              </div>
              <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
                <StatCard
                  title="Oldest Pending"
                  value={oldestPendingAge ?? '--'}
                  icon={<ClockIcon />}
                  anomaly={oldestPendingIsStale}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Tenant picker (platform users only) + filter bar */}
      <div className={cn(GLASS_FILTER, 'p-3 flex flex-wrap items-center gap-4 mb-6 animate-fade-in')}>
        {isPlatformUser && (
          <TenantPicker
            options={tenantOptions}
            value={selectedTenantId}
            onChange={setSelectedTenantId}
            isLoading={tenantsLoading}
          />
        )}
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
            Resolution
          </span>
          <div className="w-44">
            <Select
              options={RESOLUTION_OPTIONS}
              value={resolutionFilter}
              onChange={handleFilterChange}
              placeholder="All"
              clearable
              disabled={!canQuery}
            />
          </div>
        </div>
        {meta.total > 0 && (
          <div className="ml-auto">
            <Badge variant="warning" size="sm">{meta.total} total</Badge>
          </div>
        )}
      </div>

      {loadError && (
        <Alert
          variant="danger"
          title="Sync Health error"
          description={loadError}
        />
      )}

      {/* Body */}
      {!canQuery ? (
        <EmptyState
          variant="page"
          icon={<SyncEmptyStateIcon />}
          title="Pick a tenant to view sync conflicts"
          description="Platform staff inspect tenants one at a time via the tenant_id override."
        />
      ) : isLoading ? (
        <div className={cn(GLASS, 'p-6 space-y-3 animate-fade-in')}>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <Skeleton variant="rect" width={80} height={22} />
              <Skeleton variant="text" width="25%" height={16} />
              <Skeleton variant="rect" width={80} height={22} />
              <Skeleton variant="text" width="20%" height={16} />
            </div>
          ))}
        </div>
      ) : conflicts.length === 0 ? (
        <EmptyState
          variant="page"
          icon={<SyncEmptyStateIcon />}
          title="No sync conflicts"
          description="Everything is in sync for the selected tenant."
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className={cn(GLASS, 'overflow-hidden hidden sm:block mb-6 animate-fade-in')}>
            <DataTable<SyncConflict>
              columns={columns}
              data={conflicts}
              variant="default"
              currentPage={meta.page}
              totalCount={meta.total}
              pageSize={CONFLICT_PAGE_SIZE}
              onPageChange={(p) => void loadConflicts({ page: p })}
              onSort={(col, dir) => {
                // Server-side sort so the ordering spans the ENTIRE dataset,
                // not just the current page. Reset to page 1 so the globally
                // first row (e.g. oldest conflict) lands at the top.
                sortRef.current = { col, dir };
                setSortState({ col, dir });
                void loadConflicts({ page: 1 });
              }}
              selectable
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              bulkActions={bulkActions}
              loading={isBulkResolving}
              emptyState={{
                icon: <SyncEmptyStateIcon />,
                title: 'No sync conflicts',
                description: 'Everything is in sync for the selected tenant.',
              }}
            />
          </div>

          {/* Mobile cards */}
          <div className="block sm:hidden space-y-3 mb-6">
            {conflicts.map((c) => (
              <ConflictMobileCard
                key={c.id}
                conflict={c}
                isSelected={selectedConflict?.id === c.id}
                onClick={() => void handleRowClick(c.id)}
              />
            ))}
            {meta.totalPages > 1 && (
              <div className="flex justify-center pt-2">
                <Pagination
                  currentPage={meta.page}
                  totalPages={meta.totalPages}
                  totalItems={meta.total}
                  pageSize={CONFLICT_PAGE_SIZE}
                  onPageChange={(p) => void loadConflicts({ page: p })}
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* Detail drawer */}
      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        title={drawerTitle}
        position="right"
      >
        {isLoadingDetail && (
          <div className="flex items-center justify-center py-6">
            <Spinner size="sm" label="Loading conflict details..." />
          </div>
        )}
        {selectedConflict && !isLoadingDetail && (
          <ConflictDetailPanel
            key={selectedConflict.id}
            conflict={selectedConflict}
            isResolving={isResolving}
            resolvingAs={resolvingAs}
            note={note}
            onNoteChange={setNote}
            onResolve={(r, merged) => void handleResolve(r, merged)}
            onDiscard={() => setShowDiscardConfirm(true)}
            userRole={profile?.role ?? ''}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={showDiscardConfirm}
        title="Discard Change"
        description="Are you sure? This will drop the change entirely. This action cannot be undone."
        confirmLabel="Discard"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={() => { void handleDiscard(); }}
        onCancel={() => setShowDiscardConfirm(false)}
        loading={isResolving}
        confirmDisabled={!note.trim()}
      >
        {!note.trim() && (
          <p className="text-xs text-danger-600 dark:text-danger-400">Add a note in the panel first — a discard must say why.</p>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={bulkDiscardIds !== null}
        title={`Discard ${bulkDiscardIds?.length ?? 0} conflict(s)`}
        description="Each selected change is dropped. Conflicts that cannot be discarded (refused offline bills) are skipped and reported."
        confirmLabel="Discard"
        cancelLabel="Cancel"
        variant="danger"
        loading={isBulkResolving}
        confirmDisabled={!bulkDiscardNote.trim()}
        onConfirm={() => {
          const ids = bulkDiscardIds ?? [];
          const reason = bulkDiscardNote.trim();
          setBulkDiscardIds(null);
          setBulkDiscardNote('');
          void handleBulkResolve('DISCARDED', ids, reason);
        }}
        onCancel={() => { setBulkDiscardIds(null); setBulkDiscardNote(''); }}
      >
        <TextArea
          label="Note (required)"
          placeholder="Why are these being discarded?"
          value={bulkDiscardNote}
          onChange={setBulkDiscardNote}
          rows={2}
          maxLength={500}
        />
      </ConfirmDialog>
    </PageContainer>
  );
}

export default AdminSyncHealthPage;
